import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import DashLayout from "@/components/DashLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Key, Trash2 } from "lucide-react";
import { toast } from "sonner";

export default function SuperUsers() {
  const [users, setUsers] = useState([]);
  const [schools, setSchools] = useState([]);
  const [subjectsMaster, setSubjectsMaster] = useState([]);
  const [open, setOpen] = useState(false);
  const [pwdOpen, setPwdOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [f, setF] = useState({ email: "", password: "", name: "", role: "school_admin", school_id: "", subjects: [] });

  const load = async () => {
    const [u, s, sub] = await Promise.all([
      api.get("/users"),
      api.get("/schools", { params: { limit: 500 } }),
      api.get("/erp/subjects").catch(() => ({ data: [] })),
    ]);
    setUsers(u.data); setSchools(s.data); setSubjectsMaster(sub.data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/users", { ...f, school_id: f.school_id || null });
      toast.success("User created"); setOpen(false); setF({ email: "", password: "", name: "", role: "school_admin", school_id: "", subjects: [] }); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const resetPwd = async () => {
    try {
      await api.post("/auth/reset-user-password", { user_id: target.id, new_password: newPwd });
      toast.success("Password reset"); setPwdOpen(false); setNewPwd("");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const remove = async (id) => { if (!window.confirm("Delete user?")) return; await api.delete(`/users/${id}`); load(); };

  return (
    <DashLayout variant="super" title="Users">
      <div className="flex justify-between mb-6">
        <div className="text-sm text-slate-500">{users.length} users</div>
        <Button onClick={() => setOpen(true)} className="bg-slate-900 hover:bg-slate-800" data-testid="new-user-btn"><Plus className="w-4 h-4 mr-2" />Add User</Button>
      </div>
      <div className="bg-white rounded-lg border border-slate-200">
        <Table>
          <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Role</TableHead><TableHead>School (Code — Name)</TableHead><TableHead className="text-right">Actions</TableHead></TableRow></TableHeader>
          <TableBody>
            {users.map((u) => {
              const school = schools.find(s => s.id === u.school_id);
              return (
              <TableRow key={u.id} data-testid={`row-user-${u.id}`}>
                <TableCell className="font-semibold">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell><Badge variant="outline">{u.role}</Badge></TableCell>
                <TableCell className="text-xs">
                  {school ? (
                    <span>
                      <span className="font-mono font-bold text-blue-700">{school.code || "—"}</span>
                      <span className="text-slate-500"> — {school.name}</span>
                    </span>
                  ) : (
                    <span className="text-slate-400">—</span>
                  )}
                </TableCell>
                <TableCell className="text-right">
                  <button
                    type="button"
                    onClick={async () => {
                      const newStatus = u.status === "inactive" ? "active" : "inactive";
                      if (!window.confirm(`Mark ${u.name} as ${newStatus.toUpperCase()}? ${newStatus === "inactive" ? "They will not be able to log in." : "They can log in again."}`)) return;
                      try { await api.put(`/users/${u.id}/status`, { status: newStatus }); toast.success(`Marked ${newStatus}`); load(); }
                      catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
                    }}
                    data-testid={`user-status-${u.id}`}
                    className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border mr-2 ${u.status === "inactive" ? "border-red-300 text-red-700 bg-red-50" : "border-emerald-300 text-emerald-700 bg-emerald-50"}`}
                  >
                    {(u.status || "active").toUpperCase()}
                  </button>
                  <Button size="sm" variant="ghost" onClick={() => { setTarget(u); setPwdOpen(true); }} data-testid={`reset-pwd-${u.id}`}><Key className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(u.id)} data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4 text-red-600" /></Button>
                </TableCell>
              </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent><DialogHeader><DialogTitle>Add User</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4" data-testid="user-form">
            <div><Label className="mb-1 block">Name *</Label><Input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="uf-name" /></div>
            <div><Label className="mb-1 block">Email *</Label><Input type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="uf-email" /></div>
            <div><Label className="mb-1 block">Password *</Label><Input type="text" required value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="uf-password" /></div>
            <div><Label className="mb-1 block">Role</Label>
              <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v, subjects: v === "teacher" ? f.subjects : [] })}>
                <SelectTrigger data-testid="uf-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="school_admin">School Admin</SelectItem>
                  <SelectItem value="teacher">Teacher</SelectItem>
                  <SelectItem value="parent">Parent</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select></div>
            {f.role === "teacher" && (
              <div>
                <Label className="mb-1 block">Subjects taught (multi-select)</Label>
                {subjectsMaster.length === 0 ? (
                  <div className="text-xs text-slate-500 bg-slate-50 border rounded p-2">
                    No subjects yet. Add them at <b>ERP ▸ Subjects</b> first — timetable will auto-populate from teacher subjects.
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto border rounded p-2 bg-slate-50">
                    {subjectsMaster.map((s) => {
                      const active = (f.subjects || []).includes(s.id);
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => {
                            const set = new Set(f.subjects || []);
                            active ? set.delete(s.id) : set.add(s.id);
                            setF({ ...f, subjects: Array.from(set) });
                          }}
                          data-testid={`uf-subj-${s.id}`}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${active ? "bg-blue-700 text-white border-blue-700" : "bg-white text-slate-700 border-slate-300 hover:border-blue-400"}`}
                        >
                          {s.name} <span className="opacity-60">({s.code})</span>
                        </button>
                      );
                    })}
                  </div>
                )}
                <p className="text-[11px] text-slate-500 mt-1">Selected: <b>{(f.subjects || []).length}</b> · timetables will draw defaults from this list</p>
              </div>
            )}
            <div><Label className="mb-1 block">School (select by Code — Name)</Label>
              <Select value={f.school_id} onValueChange={(v) => setF({ ...f, school_id: v })}>
                <SelectTrigger data-testid="uf-school"><SelectValue placeholder="Select school by code" /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {schools.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      <span className="font-mono font-bold text-blue-700 mr-2">{s.code || "—"}</span>
                      <span>{s.name}</span>
                      <span className="text-slate-400"> · {s.city}</span>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {f.school_id && (
                <p className="text-[11px] text-slate-500 mt-1">
                  Selected code: <b className="font-mono text-slate-800">{schools.find((s) => s.id === f.school_id)?.code || "—"}</b>
                </p>
              )}
            </div>
            <DialogFooter><Button type="submit" className="bg-slate-900 hover:bg-slate-800" data-testid="uf-submit">Create</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={pwdOpen} onOpenChange={setPwdOpen}>
        <DialogContent><DialogHeader><DialogTitle>Reset password for {target?.email}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input placeholder="New password" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} data-testid="new-pwd-input" />
            <Button onClick={resetPwd} className="bg-slate-900 hover:bg-slate-800 w-full" data-testid="new-pwd-submit">Set new password</Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashLayout>
  );
}
